import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { useUser } from '../../CacheProvider';
import { __t } from '../../i18n';
import { IconLock } from '../../icons';
import { Button, Link, Text } from '../../styles';

/**
 * Why playback isn't available. `age` and `ip` are walls around the whole short; `subscription`
 * is only around this episode, and the one the viewer can do something about.
 */
export type Restriction = 'age' | 'ip' | 'subscription';

const MESSAGE: Record<Restriction, string> = {
  age: 'content.age_restricted.alert',
  ip: 'content.foreign.vpn.alert',
  subscription: 'content.watch.buysubscription',
};

type RestrictionNoticeProps = {
  reason: Restriction;
};

/**
 * Why playback isn't available. Shorts have no content page to carry that notice — and no page
 * to be redirected to — so it sits over the player instead, using the same wording.
 */
export default function RestrictionNotice({ reason }: RestrictionNoticeProps) {
  return (
    <Overlay>
      <Lock><IconLock /></Lock>
      <Text variant="body1" align="center" weight="medium">
        {__t(MESSAGE[reason])}
      </Text>
      {reason === 'subscription' && <SubscribeButton />}
    </Overlay>
  );
}

/**
 * The way out of the paywall, following the content page's own CTA: an account first when
 * there isn't one, then the packages page, both returning to the episode they were stopped on.
 */
const SubscribeButton = () => {
  const router = useRouter();
  const { authorized } = useUser();
  const target = authorized ? '/packages' : '/login';

  return (
    <Button as={Link} href={`${target}?redirect=${router.asPath}`} variant="primaryA">
      {__t(authorized ? 'common.buy.subscription' : 'content.login_and_watch')}
    </Button>
  );
};

const Overlay = styled.div(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 5,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(4),
  borderRadius: 'inherit',
  backgroundColor: theme.rgba(theme.palette.background[900][0], 0.85),
}));

const Lock = styled.span(({ theme }) => ({
  display: 'flex',
  color: theme.palette.text[900][1],
  svg: {
    width: theme.spacing(5),
    height: theme.spacing(5),
  },
}));
